import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { normalizeAppLocale, type AppLocale } from '@/constants/locales'
import { setLanguage } from '@/utils/languageSync'
import { patchPreferredLocale } from '@/apis/user'
import { useAuthStore } from '@/store/useAuthStore'
import { useDeferredReady } from '@/hooks/useDeferredReady'

/**
 * After login: DB preferred_locale wins over localStorage; backfill DB if missing.
 * PATCH locale différé (idle) pour ne pas bloquer le render post-bootstrap.
 */
export function useSyncLocale(): void {
  const { i18n } = useTranslation()
  const user = useAuthStore((s) => s.user)
  const impersonating = useAuthStore((s) => s.impersonating)
  const syncedRef = useRef<string | null>(null)
  const idleReady = useDeferredReady(1000)

  useEffect(() => {
    if (!idleReady || !user?.id || impersonating) {
      return
    }

    const syncKey = `${user.id}:${user.preferred_locale ?? 'null'}`
    if (syncedRef.current === syncKey) {
      return
    }

    const frontLocale = normalizeAppLocale(i18n.language)
    const dbRaw = user.preferred_locale as string | undefined | null
    const dbLocale =
      dbRaw != null && dbRaw !== ''
        ? normalizeAppLocale(dbRaw)
        : null

    const run = async () => {
      if (dbLocale && dbLocale !== frontLocale) {
        setLanguage(dbLocale)
        syncedRef.current = `${user.id}:${dbLocale}`
        return
      }

      if (!dbLocale) {
        try {
          await patchPreferredLocale(frontLocale)
          useAuthStore.getState().setProfile({
            ...user,
            preferred_locale: frontLocale,
          })
        } catch (err) {
          console.error('[useSyncLocale] Failed to backfill preferred_locale:', err)
        }
      }

      syncedRef.current = syncKey
    }

    void run()
  }, [user, i18n.language, impersonating, idleReady])
}

export function getProfilePreferredLocale(user: { preferred_locale?: string | null } | null): AppLocale | null {
  if (!user?.preferred_locale) {
    return null
  }
  return normalizeAppLocale(user.preferred_locale)
}
